import { FC, memo, useContext, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  CheckedCalendarIcon,
  DashManIcon,
  Preloader,
  ShutdownIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { SessionRestTimer } from 'components';
import { DEFAULT_GROUP_COLOR } from 'consts';
import { useDoubleClick, useSendShellCommand } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { checkShellVersion } from 'utils';

import s from './Host.module.scss';
import {
  IHostProps,
  HostGroupRequest,
  HostGroupResponse,
  HostRequest,
  GetHostsResponse,
} from './interfaces';
import { GET_HOST, HOST_GROUP } from './schema';
import { Tooltip } from './Tooltip';
import { getHostState } from './utiltes';

export const Host: FC<IHostProps> = memo(
  ({
    id,
    selected,
    onContextMenu,
    onHostClick,
    onHostDoubleClick,
    openBooking,
    canOpenTooltip,
    setClientUUID,
    isSelecting,
    setSelectedItems,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
  }) => {
    const { t } = useContext(LocalizationContext);
    const {
      data: { permissions },
    } = useContext(AuthContext);
    const hostRef = useRef<HTMLDivElement>(null);
    const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
    const [isTurningOn, setIsTurningOn] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

    const { sendCommand } = useSendShellCommand();

    const { data: hostResponse } = useQuery<GetHostsResponse, HostRequest>(
      GET_HOST,
      {
        fetchPolicy: 'cache-only',
        variables: { id },
      }
    );

    const { data: groupResponse } = useQuery<
      HostGroupResponse,
      HostGroupRequest
    >(HOST_GROUP, {
      fetchPolicy: 'cache-only',
      variables: { id: hostResponse?.hostOverview.group_id ?? 0 },
      skip: !hostResponse?.hostOverview,
    });

    if (!hostResponse?.hostOverview) return null;

    const {
      position,
      client_sessions: [activeSession],
      user,
      bookings,
      online,
      locked,
      info,
    } = hostResponse.hostOverview;

    const hostState = getHostState(hostResponse.hostOverview);
    const Icon = hostState?.Icon;
    const remainTime = activeSession?.time_left;

    const cardColor =
      hostState?.color ||
      groupResponse?.hostGroupOverview.customization?.color ||
      DEFAULT_GROUP_COLOR;

    const clickFunc = useDoubleClick(
      (event, id) => onHostClick(event, id),
      (id) => {
        if (!permissions?.has('payment_create')) {
          return notification.info({
            t,
            title: t('permission.error'),
            type: 'warning',
            dismiss: 5,
          });
        }

        if (
          hostResponse &&
          hostResponse.hostOverview.client_sessions[0]?.postpaid
        ) {
          return notification.info({
            t,
            title: 'Нельзя открыть магазин на данном ПК',
            type: 'warning',
            dismiss: 5,
          });
        }

        onHostDoubleClick(id);
      }
    );

    return (
      <div
        ref={hostRef}
        data-id={id}
        onClick={(event) => {
          clickFunc(event, id);
        }}
        onContextMenu={(e) => {
          if (!selected) {
            const itemId = e.currentTarget.getAttribute('data-id');
            if (itemId) {
              setSelectedItems(new Set([itemId]));
            }
          }
          onHostClick(e, id);
          onContextMenu(e, hostResponse.hostOverview);

          setIsTooltipOpen(false);
        }}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          if (!touch) return;

          const fakeMouseEvent = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            clientX: touch.clientX,
            clientY: touch.clientY,
            button: 2,
          });

          onHostClick(fakeMouseEvent as unknown as React.MouseEvent, id);

          onTouchStart(event, hostResponse.hostOverview);
        }}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseEnter={() => {
          if (!canOpenTooltip || !hostRef.current) return;

          if (tooltipTimer.current) {
            clearTimeout(tooltipTimer.current);
            tooltipTimer.current = null;
          }

          requestAnimationFrame(() => {
            if (!hostRef.current) return;

            setAnchorRect(hostRef.current.getBoundingClientRect());
            setIsTooltipOpen(true);
          });
        }}
        onMouseLeave={() => {
          tooltipTimer.current = setTimeout(() => setIsTooltipOpen(false), 50);
        }}
        className={cn(
          s.container,
          !online && !hostState && s.offline,
          activeSession && s.session,
          selected && s.selected,
          locked && s.locked,
          isSelecting && s.isSelecting
        )}
        style={{
          // @ts-ignore it's ok to set css var in style
          '--group-color': cardColor,
          '--group-color-10': cardColor + '1A',
          '--group-color-20': cardColor + '33',
          '--group-color-30': cardColor + '99',
        }}
      >
        {!isSelecting && isTooltipOpen && anchorRect && (
          <Tooltip
            id={id}
            setClientUUID={setClientUUID}
            openBooking={(bookingId) => {
              setIsTooltipOpen(false);
              openBooking(bookingId);
            }}
            anchorRect={anchorRect}
          />
        )}
        <div className={s.top}>
          <span className={s.num}>
            {user?.roles.length ? <DashManIcon size="s" /> : position}
          </span>
          {hostState && !bookings.length && (
            <div className={cn(s.label, hostState.hideBG && s.ghostBG)}>
              {Icon && (
                <Icon
                  {...(hostState.hideBG ? {} : { size: 's' })}
                  color="currentColor"
                />
              )}
            </div>
          )}
          {Boolean(bookings.length) && (
            <Button
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                openBooking(bookings[0].id);
              }}
              className={cn(
                !hostState &&
                  !online &&
                  checkShellVersion(info?.shell_version) &&
                  permissions?.has('host_command_manage') &&
                  s.booking_hover,
                s.button
              )}
              disabled={bookings[0].byClient}
            >
              <div className={s.label}>
                <CheckedCalendarIcon size="s" />
              </div>
            </Button>
          )}
          {!bookings.length &&
            !hostState &&
            !online &&
            checkShellVersion(info?.shell_version) &&
            permissions?.has('host_command_manage') && (
              <Button
                variant="ghost"
                disabled={isTurningOn}
                size="s"
                className={cn(s.button)}
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsTurningOn(true);

                  await sendCommand({ host_id: id, action: 'WOL' });

                  return setIsTurningOn(false);
                }}
              >
                <div className={s.turnOn}>
                  <Preloader loading={isTurningOn} className={s.turnOnLoader}>
                    <ShutdownIcon size="s" />
                  </Preloader>
                </div>
              </Button>
            )}
          {(Boolean(bookings.length) || hostState) &&
            !hostState &&
            !online &&
            checkShellVersion(info?.shell_version) &&
            permissions?.has('host_command_manage') && (
              <Button
                variant="ghost"
                disabled={isTurningOn}
                size="s"
                className={cn(s.buttons, s.hover_button)}
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsTurningOn(true);

                  await sendCommand({ host_id: id, action: 'WOL' });

                  return setIsTurningOn(false);
                }}
              >
                <div className={s.turnOn}>
                  <Preloader loading={isTurningOn} className={s.turnOnLoader}>
                    <ShutdownIcon size="s" />
                  </Preloader>
                </div>
              </Button>
            )}
        </div>
        {activeSession && (
          <div className={s.session}>
            {activeSession.finished_at ? (
              <SessionRestTimer
                rest={remainTime}
                before5MinutesPlaceholder={dayjs(
                  activeSession.finished_at
                ).format('HH:mm')}
                sessionId={activeSession.id}
                showDot
                showMinimal={true}
              />
            ) : (
              '∞'
            )}
          </div>
        )}
      </div>
    );
  }
);
